import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import LinksComponent from "../../../../components/Tenant/LinksComponent/LinksComponent";
import DataTableComponent from "../../../../components/Tenant/DataTableComponent/DataTableComponent";
import ExportCSV from "../../../../components/ExportCsv/ExportCsv";
import { useGetBontonQrLocationMutation } from "../../../../features/supplyBeam/supplyBeamData/checkoutListAPI";
import { FiSearch, FiRefreshCw, FiMapPin, FiCalendar, FiUser, FiPackage, FiFilter } from "react-icons/fi";

const BontonQrTracking = () => {
  const initialFilters = {
    page: 1,
    limit: 10,
    order_no: "",
    seller_code: "",
    buyer_code: "",
    status: "",
    start_date: "",
    end_date: "",
    batch_running_code: "",
  };

  const [filters, setFilters] = useState(initialFilters);
  const [listData, setListData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const [getBontonQrLocation, { isLoading }] = useGetBontonQrLocationMutation();

  const fetchQrLocations = async (overrideParams = {}) => {
    const token = sessionStorage.getItem("access_token");
    const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
    const tenant_id = vendorData?.tenant_id;

    if (!tenant_id) {
      toast.error("Tenant ID missing. Please log in again.");
      return;
    }

    const payload = {
      ...filters,
      page: currentPage,
      limit: perPage,
      ...overrideParams,
    };

    try {
      const response = await getBontonQrLocation({
        token,
        tenant_id,
        body: payload,
      });

      if (response?.data?.success) {
        const resBody = response?.data?.data || response?.data?.body || {};
        const rows = Array.isArray(resBody) ? resBody : resBody?.list || [];
        const total = resBody?.pagination?.total ?? (Array.isArray(resBody) ? resBody.length : rows.length);

        setListData(rows);
        setTotalRows(total);
      } else {
        const errorMsg = response?.error?.data?.message || response?.data?.message || "Failed to fetch QR locations";
        toast.error(errorMsg);
        setListData([]);
        setTotalRows(0);
      }
    } catch (error) {
      console.error("Error fetching QR locations:", error);
      toast.error("An error occurred while fetching QR locations");
      setListData([]);
      setTotalRows(0);
    }
  };

  useEffect(() => {
    fetchQrLocations();
  }, [currentPage, perPage]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchQrLocations({ page: 1 });
  };

  const handleReset = () => {
    setFilters(initialFilters);
    setCurrentPage(1);
    fetchQrLocations({ ...initialFilters, page: 1 });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePerRowsChange = (newPerPage, page) => {
    setPerPage(newPerPage);
    setCurrentPage(page);
  };

  const columns = [
    {
      name: "S.No",
      selector: (row, idx) => (currentPage - 1) * perPage + idx + 1,
      sortable: false,
      width: "70px",
    },
    {
      name: "Batch Running Code",
      selector: (row) => row.qr_batch_running_code || row.qr_unique_code || "N/A",
      sortable: true,
      minWidth: "160px",
      cell: (row) => (
        <div className="font-semibold text-gray-800">
          {row.qr_batch_running_code || row.qr_unique_code || "N/A"}
          {row.qr_unique_code && (
            <div className="text-xs font-normal text-gray-500">{row.qr_unique_code}</div>
          )}
        </div>
      ),
    },
    {
      name: "QR Type",
      selector: (row) => row.qr_type,
      sortable: true,
      width: "120px",
      cell: (row) => {
        const typeMap = {
          "1": { label: "Child", color: "bg-blue-100 text-blue-800 border-blue-200" },
          "2": { label: "Parent", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
          "3": { label: "Carton", color: "bg-amber-100 text-amber-800 border-amber-200" },
        };
        const badge = typeMap[String(row.qr_type)] || {
          label: row.qr_type || "N/A",
          color: "bg-gray-100 text-gray-700 border-gray-200",
        };
        return (
          <span className={`px-2.5 py-1 text-xs font-semibold rounded-full border ${badge.color}`}>
            {badge.label}
          </span>
        );
      },
    },
    {
      name: "Scan Status",
      selector: (row) => row.is_qr_in_or_out,
      sortable: true,
      width: "120px",
      cell: (row) => {
        const status = String(row.is_qr_in_or_out || "").toUpperCase();
        const isIN = status === "IN";
        return (
          <span
            className={`inline-flex items-center px-2.5 py-1 text-xs font-bold rounded-full border ${
              isIN
                ? "bg-green-100 text-green-800 border-green-300"
                : "bg-purple-100 text-purple-800 border-purple-300"
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                isIN ? "bg-green-500 animate-pulse" : "bg-purple-500"
              }`}
            ></span>
            {status || "N/A"}
          </span>
        );
      },
    },
    {
      name: "Current Location",
      selector: (row) => row.current_location,
      sortable: true,
      minWidth: "160px",
      cell: (row) => (
        <div className="flex items-start gap-1">
          <FiMapPin className="text-red-500 mt-1 flex-shrink-0" />
          <div>
            <div className="font-semibold text-gray-900">{row.current_location || "N/A"}</div>
            {row.location_code && (
              <div className="text-xs text-gray-500">Code: {row.location_code}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      name: "Address",
      selector: (row) => row.current_adress,
      sortable: true,
      minWidth: "180px",
      cell: (row) => (
        <span className="text-sm text-gray-600 truncate max-w-xs" title={row.current_adress}>
          {row.current_adress || "N/A"}
        </span>
      ),
    },
    {
      name: "Scanned By",
      selector: (row) => row.user_name,
      sortable: true,
      minWidth: "180px",
      cell: (row) => (
        <div className="flex items-start gap-1.5">
          <FiUser className="text-blue-600 mt-1 flex-shrink-0" />
          <div>
            <div className="font-medium text-gray-900">{row.user_name || "N/A"}</div>
            <div className="text-xs text-gray-500">{row.user_email || row.user_mobile || ""}</div>
          </div>
        </div>
      ),
    },
    {
      name: "Role & Emp ID",
      selector: (row) => row.employee_id,
      sortable: true,
      minWidth: "140px",
      cell: (row) => (
        <div>
          <div className="text-xs font-semibold text-gray-800">{row.designation || "N/A"}</div>
          {row.employee_id && (
            <div className="text-xs text-gray-500">ID: {row.employee_id}</div>
          )}
        </div>
      ),
    },
    {
      name: "Scanned Date",
      selector: (row) => row.scanned_at || row.created_at,
      sortable: true,
      minWidth: "160px",
      cell: (row) => {
        const rawDate = row.scanned_at || row.created_at;
        return (
          <div className="text-xs text-gray-600 flex items-center gap-1">
            <FiCalendar className="text-gray-400" />
            {rawDate ? new Date(rawDate).toLocaleString() : "N/A"}
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex w-full bg-gray-100 min-h-screen justify-center">
      <div className="w-full">
        {/* Page Header */}
        <div className="border-b bg-white border-gray-300 px-6 py-4 shadow-sm text-2xl font-bold flex justify-between items-center text-gray-800 tracking-tight">
          <div className="flex items-center gap-2">
            <FiPackage className="text-blue-600" />
            Bonton QR Location Tracking
          </div>
          <LinksComponent />
        </div>

        <div className="p-6 max-w-7xl mx-auto space-y-6">
          {/* Filters Form */}
          <div className="bg-white shadow-md rounded-2xl p-6 border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-5">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FiFilter className="text-blue-600" />
                Filter QR Locations
              </h2>
              {listData.length > 0 && (
                <ExportCSV
                  data={listData}
                  columnsToExport={[
                    "qr_batch_running_code",
                    "qr_unique_code",
                    "qr_type",
                    "is_qr_in_or_out",
                    "current_location",
                    "current_adress",
                    "location_code",
                    "user_name",
                    "user_email",
                    "user_mobile",
                    "employee_id",
                    "designation",
                    "scanned_at",
                  ]}
                />
              )}
            </div>

            <form onSubmit={handleSearch} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {/* Batch Running Code */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Batch / QR Code
                  </label>
                  <input
                    type="text"
                    name="batch_running_code"
                    value={filters.batch_running_code}
                    onChange={handleInputChange}
                    placeholder="Enter Batch/QR Code"
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                  />
                </div>

                {/* Order No */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Order No
                  </label>
                  <input
                    type="text"
                    name="order_no"
                    value={filters.order_no}
                    onChange={handleInputChange}
                    placeholder="Enter Order No"
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                  />
                </div>

                {/* Seller Code */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Seller Code
                  </label>
                  <input
                    type="text"
                    name="seller_code"
                    value={filters.seller_code}
                    onChange={handleInputChange}
                    placeholder="Enter Seller Code"
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                  />
                </div>

                {/* Buyer Code */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Buyer Code
                  </label>
                  <input
                    type="text"
                    name="buyer_code"
                    value={filters.buyer_code}
                    onChange={handleInputChange}
                    placeholder="Enter Buyer Code"
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Status
                  </label>
                  <select
                    name="status"
                    value={filters.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                  >
                    <option value="">All Statuses</option>
                    <option value="IN">IN</option>
                    <option value="OUT">OUT</option>
                  </select>
                </div>

                {/* Start Date */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    name="start_date"
                    value={filters.start_date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                  />
                </div>

                {/* End Date */}
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    name="end_date"
                    value={filters.end_date}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 text-sm text-gray-900 border border-gray-300 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
                  />
                </div>

                {/* Action Buttons */}
                <div className="flex items-end gap-3 col-span-1 sm:col-span-2 lg:col-span-1">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="flex-1 flex items-center justify-center gap-2 text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 font-semibold rounded-xl text-sm px-4 py-2.5 transition-all shadow-md hover:shadow-lg disabled:opacity-50 cursor-pointer"
                  >
                    <FiSearch />
                    {isLoading ? "Searching..." : "Apply"}
                  </button>

                  <button
                    type="button"
                    onClick={handleReset}
                    className="flex items-center justify-center gap-1.5 text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 border border-gray-300 font-semibold rounded-xl text-sm px-4 py-2.5 transition-all cursor-pointer"
                  >
                    <FiRefreshCw />
                    Reset
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Listing / DataTable Card */}
          <div className="bg-white shadow-md rounded-2xl overflow-hidden border border-gray-200 p-4">
            <DataTableComponent
              columns={columns}
              data={listData}
              loading={isLoading}
              totalRows={totalRows}
              paginationPerPage={perPage}
              paginationDefaultPage={currentPage}
              handlePageChange={handlePageChange}
              handlePerRowsChange={handlePerRowsChange}
              keyField="id"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default BontonQrTracking;